import { useState, useEffect } from 'react';

const FETCH_TIMEOUT_MS = 10000;

type ResourceStatus = 'idle' | 'loading' | 'success' | 'error' | 'timeout';

/** Fetches the worker-produced video-signals JSON (audio/motion envelopes,
 *  keyframe-decoded scene cuts, energy peaks, content-type classification)
 *  pointed at by `project.video_signals_url`.
 *
 *  Same fetch/abort/status/error-report structure as useSceneDetection. A null
 *  or empty URL is a clean no-op: `idle` status, `null` data, no request, no
 *  error — which is what every project created before the signals stage looks
 *  like. */
export function useVideoSignals(videoSignalsUrl: string | null | undefined) {
  const [signals, setSignals] = useState<unknown>(null);
  const [loading, setLoading] = useState<boolean>(!!videoSignalsUrl);
  const [status, setStatus] = useState<ResourceStatus>(videoSignalsUrl ? 'loading' : 'idle');
  const [error, setError] = useState<Error | null>(null);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!videoSignalsUrl) {
      setSignals(null);
      setLoading(false);
      setStatus('idle');
      setError(null);
      setTimedOut(false);
      return;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort('timeout'), FETCH_TIMEOUT_MS);

    setLoading(true);
    setStatus('loading');
    setError(null);
    setTimedOut(false);

    let cancelled = false;
    const startedAt = performance.now();

    fetch(videoSignalsUrl, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Video signals request failed with status ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        if (!cancelled) {
          setSignals(data);
          setLoading(false);
          setStatus('success');
          console.info('[clips] video signals loaded', {
            url: videoSignalsUrl,
            durationMs: Math.round(performance.now() - startedAt),
          });
        }
      })
      .catch((err) => {
        if (!cancelled) {
          const didTimeOut =
            err?.name === 'AbortError' || err === 'timeout' || err?.message === 'timeout';
          setSignals(null);
          setLoading(false);
          setStatus(didTimeOut ? 'timeout' : 'error');
          setError(err instanceof Error ? err : new Error(String(err)));
          setTimedOut(didTimeOut);
          console.warn('[clips] video signals fetch failed', {
            url: videoSignalsUrl,
            durationMs: Math.round(performance.now() - startedAt),
            timedOut: didTimeOut,
            message: err instanceof Error ? err.message : String(err),
          });
        }
      })
      .finally(() => {
        window.clearTimeout(timeoutId);
      });

    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [videoSignalsUrl]);

  return { data: signals, loading, status, error, timedOut };
}
